/**
 * Componente de card para exibir status de atividade dos usuários
 * Para uso no dashboard de supervisores/admins
 */
import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Chip,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  CircularProgress
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  People as PeopleIcon,
  Person as PersonIcon,
  PersonOff as PersonOffIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccessTime as AccessTimeIcon,
  Assignment as AssignmentIcon
} from '@material-ui/icons';
import { useTimeMonitoring } from '../../hooks/useTimeMonitoring';
import { i18n } from '../../translate/i18n';

const useStyles = makeStyles((theme) => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardContent: {
    flexGrow: 1,
    padding: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  headerIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  summaryContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  summaryItem: {
    textAlign: 'center',
  },
  summaryValue: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.5),
  },
  summaryLabel: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  expandButton: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    paddingTop: theme.spacing(1),
  },
  userList: {
    maxHeight: 300,
    overflowY: 'auto',
  },
  userItem: {
    padding: `${theme.spacing(1)}px 0`,
  },
  userAvatar: {
    width: 32,
    height: 32,
    fontSize: '0.9rem',
  },
  statusChip: {
    minWidth: 60,
    height: 20,
    fontSize: '0.7rem',
  },
  inactiveTime: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  activeStatus: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  inactiveStatus: {
    backgroundColor: '#ff9800',
    color: 'white',
  },
  offlineStatus: {
    backgroundColor: '#9e9e9e',
    color: 'white',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 120,
  }
}));

const UserActivityCard = () => {
  const classes = useStyles();
  const { getUsersActivitySummary, getAllUsersActivityStatus } = useTimeMonitoring();
  
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  // Carregar dados periodicamente
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [summaryData, usersData] = await Promise.all([
          getUsersActivitySummary(),
          getAllUsersActivityStatus()
        ]);
        setSummary(summaryData);
        setUsers(usersData);
      } catch (error) {
        console.error('Error loading user activity data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Atualizar a cada 30 segundos
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [getUsersActivitySummary, getAllUsersActivityStatus]);

  // Formatar tempo de inatividade
  const formatInactiveTime = (minutes) => {
    if (minutes < 60) {
      return `${minutes}min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}min` : `${hours}h`;
  };

  // Obter classe CSS do status
  const getStatusClass = (status) => {
    switch (status) {
      case 'active':
        return classes.activeStatus;
      case 'inactive':
        return classes.inactiveStatus;
      case 'offline':
      default:
        return classes.offlineStatus;
    }
  };

  // Obter texto do status
  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return i18n.t("timeMonitoring.dashboard.userActivity.userStatus.active");
      case 'inactive':
        return i18n.t("timeMonitoring.dashboard.userActivity.userStatus.inactive");
      case 'offline':
      default:
        return i18n.t("timeMonitoring.dashboard.userActivity.userStatus.offline");
    }
  };

  if (loading) {
    return (
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!summary) {
    return null;
  }

  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        {/* Header */}
        <Box className={classes.header}>
          <PeopleIcon className={classes.headerIcon} />
          <Typography variant="h6">
            {i18n.t("timeMonitoring.dashboard.userActivity.title")}
          </Typography>
        </Box>

        {/* Summary */}
        <Box className={classes.summaryContainer}>
          <div className={classes.summaryItem}>
            <div className={`${classes.summaryValue} ${classes.activeStatus}`}>
              {summary.activeUsers}
            </div>
            <div className={classes.summaryLabel}>{i18n.t("timeMonitoring.dashboard.userActivity.active")}</div>
          </div>
          
          <div className={classes.summaryItem}>
            <div className={`${classes.summaryValue} ${classes.inactiveStatus}`}>
              {summary.inactiveUsers}
            </div>
            <div className={classes.summaryLabel}>{i18n.t("timeMonitoring.dashboard.userActivity.inactive")}</div>
          </div>
          
          <div className={classes.summaryItem}>
            <div className={`${classes.summaryValue} ${classes.offlineStatus}`}>
              {summary.offlineUsers}
            </div>
            <div className={classes.summaryLabel}>{i18n.t("timeMonitoring.dashboard.userActivity.offline")}</div>
          </div>

          <div className={classes.summaryItem}>
            <div className={classes.summaryValue} style={{ color: '#2196f3' }}>
              {summary.totalActiveTickets}
            </div>
            <div className={classes.summaryLabel}>{i18n.t("timeMonitoring.dashboard.userActivity.tickets")}</div>
          </div>
        </Box>

        <Divider />

        {/* Expand Button */}
        <Box className={classes.expandButton}>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            size="small"
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            <Typography variant="caption" style={{ marginLeft: 4 }}>
              {expanded 
                ? i18n.t("timeMonitoring.dashboard.userActivity.hideDetails") 
                : i18n.t("timeMonitoring.dashboard.userActivity.showDetails")
              }
            </Typography>
          </IconButton>
        </Box>

        {/* Detailed User List */}
        <Collapse in={expanded}>
          <List className={classes.userList} dense>
            {users.map((user, index) => (
              <React.Fragment key={user.id}>
                <ListItem className={classes.userItem}>
                  <ListItemAvatar>
                    <Avatar className={classes.userAvatar}>
                      {user.name.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="body2" noWrap>
                          {user.name}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={getStatusText(user.status)}
                            size="small"
                            className={`${classes.statusChip} ${getStatusClass(user.status)}`}
                          />
                          {user.currentActiveTickets > 0 && (
                            <Chip
                              label={`${user.currentActiveTickets} tickets`}
                              size="small"
                              icon={<AssignmentIcon style={{ fontSize: 12 }} />}
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Box>
                    }
                    secondary={
                      user.status === 'inactive' && user.inactiveMinutes > 0 && (
                        <Typography className={classes.inactiveTime}>
                          <AccessTimeIcon style={{ fontSize: 12, marginRight: 4 }} />
                          {i18n.t("timeMonitoring.dashboard.userActivity.inactiveFor")} {formatInactiveTime(user.inactiveMinutes)}
                        </Typography>
                      )
                    }
                  />
                </ListItem>
                {index < users.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default UserActivityCard;